import { MasterOutletFormInstance, OutletDistributorRow } from '../types';

export type ChangeFieldKey = keyof MasterOutletFormInstance;

export type DistributorRowChangeStatus = 'unchanged' | 'added' | 'modified' | 'removed';

export type UpdateDistributor = {
  id: number;
  is_general: boolean;
};

export type ApplyDistributorRow = OutletDistributorRow & {
  changeStatus: DistributorRowChangeStatus;
  selectable: boolean;
};

export type DefaultSelection = 'all' | 'none';

const EXCLUDED_FIELD_KEYS = new Set<ChangeFieldKey>([
  'distributor',
  'updater_user',
]);

export const OUTLET_FIELD_LABELS: Partial<Record<ChangeFieldKey, string>> = {
  id: 'ID',
  parent: 'Предшественник (ТТ)',
  parent_id: 'ID предшественника',
  sales_source: 'Источник продаж',
  sales_outlet_id: 'ID ТТ источника продаж',
  outlet_acb_id: 'ID заявки ACB',
  sfa_id: 'SFA ID',
  current_sfa_id: 'ID ТТ в SFA',
  dist_system_id: 'ID ТТ в системе дистрибьютора',
  unique_hash: 'Уникальный хеш',
  status: 'Статус',
  user: 'Пользователь',
  name: 'Наименование',
  legal_name: 'Юридическое наименование',
  legal_address: 'Юридический адрес',
  address: 'Фактический адрес',
  geocodedAddress: 'Геокодированный адрес',
  inn: 'ИНН',
  ogrn: 'ОГРН',
  kpp: 'КПП',
  okpo: 'ОКПО',
  bic: 'БИК банка',
  bank: 'Банк',
  corr_account: 'Кор.счет',
  account: 'Р/счет',
  payment_type: 'Тип оплаты',
  payment_deferment: 'Отсрочка платежа',
  title: 'Вывеска',
  subnet: 'Подсеть',
  channel: 'Канал',
  sub_channel: 'Подканал',
  address_id: 'ID адреса',
  supervisor: 'Супервайзер',
  phone: 'Телефон',
  email: 'Email',
  route_number: 'Номер маршрута',
  visits_day: 'День визита в ТТ',
  director: 'Директор - ФИО',
  contact_person_1: 'Контактное лицо (1)',
  contact_person_2: 'Контактное лицо (2)',
  price: 'Прайс-лист',
  photos: 'Фото',
  division: 'Дивизион',
  segment: 'Сегмент',
  visits_frequency: 'Частота посещений',
  equipment_doors_number: 'Количество дверей оборудования',
  create_dt: 'Дата создания',
  update_dt: 'Дата обновления',
  open_date: 'Дата открытия',
  close_date: 'Дата закрытия',
  acb_user: 'Пользователь ACB',
  master_user: 'Мастер-пользователь',
  updater_user: 'Заявитель',
  count_equipment: 'Количество оборудования',
  count_stand: 'Количество стоек',
  comment: 'Комментарии',
  is_closed_for_visit: 'Недоступно к посещению',
  room_type: 'Тип',
  latlng: 'Координаты',
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const isFieldEqual = (left: unknown, right: unknown): boolean => {
  if (left === right) {
    return true;
  }

  if (left === null || left === undefined || right === null || right === undefined) {
    return (left ?? null) === (right ?? null);
  }

  if (Array.isArray(left) && Array.isArray(right)) {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((item, index) => isFieldEqual(item, right[index]));
  }

  if (isPlainObject(left) && isPlainObject(right)) {
    if ('id' in left && 'id' in right && left.id !== undefined && right.id !== undefined) {
      return String(left.id) === String(right.id);
    }

    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);

    if (leftKeys.length !== rightKeys.length) {
      return false;
    }

    return leftKeys.every((key) => isFieldEqual(left[key], right[key]));
  }

  return false;
};

export const getFieldLabel = (key: ChangeFieldKey) => (
  OUTLET_FIELD_LABELS[key] || key
);

export const formatFieldValue = (key: ChangeFieldKey, value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'Да' : 'Нет';
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (Array.isArray(value)) {
    if (key === 'latlng' && value.length === 2) {
      return `${value[0]}, ${value[1]}`;
    }

    return value.length ? `${value.length} шт.` : '—';
  }

  if (isPlainObject(value)) {
    if (typeof value.full_address === 'string' && value.full_address) {
      return value.full_address;
    }

    if (typeof value.name === 'string' && value.name) {
      return value.name;
    }

    if (typeof value.full_name === 'string' && value.full_name) {
      const username = typeof value.username === 'string' ? value.username : '';
      return username ? `${value.full_name} (${username})` : value.full_name;
    }

    if (value.id !== undefined) {
      return String(value.id);
    }
  }

  return String(value);
};

const isEmptyAddressValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '' || value === '<nil>') {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0 || value.every(isEmptyAddressValue);
  }

  if (isPlainObject(value)) {
    if ('name' in value) {
      return isEmptyAddressValue(value.name);
    }
    const values = Object.values(value);
    return values.length === 0 || values.every(isEmptyAddressValue);
  }

  return false;
};

/** Адрес со всеми пустыми полями считаем «без изменений». */
const isEmptyAddress = (address: unknown): boolean => {
  if (!address || typeof address !== 'object') {
    return true;
  }
  return Object.values(address as Record<string, unknown>).every(isEmptyAddressValue);
};

export const getChangedFieldKeys = (
  origin?: Partial<MasterOutletFormInstance>,
  instance?: Partial<MasterOutletFormInstance>
): ChangeFieldKey[] => {
  if (!origin || !instance) {
    return [];
  }

  const allKeys = new Set<ChangeFieldKey>([
    ...(Object.keys(origin) as ChangeFieldKey[]),
    ...(Object.keys(instance) as ChangeFieldKey[]),
  ]);

  return [...allKeys]
    .filter((key) => !EXCLUDED_FIELD_KEYS.has(key))
    .filter((key) => {
      if (key === 'address' && isEmptyAddress(instance.address)) {
        return false;
      }
      if ((key === 'sub_channel' || key === 'visits_day') && instance[key] === undefined) {
        return false;
      }
      return !isFieldEqual(origin[key], instance[key]);
    });
};

const sortByGeneral = <T extends OutletDistributorRow>(rows: T[]) => (
  [...rows].sort((a, b) => Number(b.is_general) - Number(a.is_general))
);

export const getApplyDistributorRows = (
  currentDistributors: OutletDistributorRow[],
  nextDistributors?: UpdateDistributor[],
  distributorNames?: Map<number, string>
): ApplyDistributorRow[] => {
  if (!nextDistributors?.length) {
    return sortByGeneral(currentDistributors).map((row) => ({
      ...row,
      changeStatus: 'unchanged' as const,
      selectable: false,
    }));
  }

  const currentById = new Map(
    currentDistributors.map((row) => [row.distributor_id, row])
  );
  const nextById = new Map(
    nextDistributors.map((item) => [item.id, item])
  );

  const allIds = new Set([
    ...currentDistributors.map((row) => row.distributor_id),
    ...nextDistributors.map((item) => item.id),
  ]);

  const rows: ApplyDistributorRow[] = [];

  allIds.forEach((distributorId) => {
    const current = currentById.get(distributorId);
    const next = nextById.get(distributorId);

    if (current && next) {
      if (current.is_general !== next.is_general) {
        rows.push({
          ...current,
          is_general: next.is_general,
          changeStatus: 'modified',
          selectable: true,
          _rowHighlight: next.is_general ? 'new-general' : undefined,
        });
        return;
      }

      rows.push({
        ...current,
        changeStatus: 'unchanged',
        selectable: false,
      });
      return;
    }

    if (next && !current) {
      rows.push({
        distributor_id: next.id,
        distributor_name: distributorNames?.get(next.id) || '',
        distributor_outlet_id: '',
        sfa_id: 0,
        is_general: next.is_general,
        is_active: true,
        changeStatus: 'added',
        selectable: true,
        _rowHighlight: next.is_general ? 'new-general' : undefined,
      });
      return;
    }

    if (current && !next) {
      rows.push({
        ...current,
        is_active: false,
        changeStatus: 'removed',
        selectable: true,
      });
    }
  });

  return sortByGeneral(rows);
};

export const getSelectableDistributorIds = (rows: ApplyDistributorRow[]) => (
  rows.filter((row) => row.selectable).map((row) => row.distributor_id)
);

export const buildInitialSelection = (
  changedFields: ChangeFieldKey[],
  distributorIds: number[],
  defaultSelection: DefaultSelection
) => {
  const selected = defaultSelection === 'all';
  return {
    fields: Object.fromEntries(changedFields.map((key) => [key, selected])) as SelectedFields,
    distributors: Object.fromEntries(distributorIds.map((id) => [id, selected])) as Record<number, boolean>,
  };
};

export type SelectedFields = Partial<Record<ChangeFieldKey, boolean>>;

const CONTACT_UNCHANGED_TOKENS = new Set(['[unchanged]', '[unchenged]']);

const isContactPersonKey = (key: ChangeFieldKey) =>
  key === 'contact_person_1' || key === 'contact_person_2';

/** Подставляет origin-значение в части contact_person, где пришло [unchanged]. */
export const resolveContactPersonValue = (
  newValue: unknown,
  originValue: unknown
): string => {
  const newParts = typeof newValue === 'string' ? newValue.split('|') : [];
  const originParts = typeof originValue === 'string' ? originValue.split('|') : [];

  return [0, 1, 2].map((index) => {
    const part = newParts[index] ?? '';
    if (CONTACT_UNCHANGED_TOKENS.has(part)) {
      const originPart = originParts[index] ?? '';
      return originPart === '<nil>' ? '' : originPart;
    }
    return part === '<nil>' ? '' : part;
  }).join('|');
};

export const buildAppliedInstance = (
  origin: Partial<MasterOutletFormInstance>,
  instance: Partial<MasterOutletFormInstance>,
  selectedFields: SelectedFields,
  applyDistributor: boolean
): Partial<MasterOutletFormInstance> => {
  const result: Partial<MasterOutletFormInstance> = {
    ...origin,
    updater_user: instance.updater_user,
  };

  (Object.keys(selectedFields) as ChangeFieldKey[]).forEach((key) => {
    if (selectedFields[key]) {
      if (isContactPersonKey(key)) {
        (result as Record<string, unknown>)[key] = resolveContactPersonValue(
          instance[key],
          origin[key]
        );
      } else {
        (result as Record<string, unknown>)[key] = instance[key];
      }
    }
  });

  if (applyDistributor) {
    result.distributor = instance.distributor;
  }

  return result;
};

export const hasEquipment = (outlet?: Partial<MasterOutletFormInstance>) => {
  const equipment = Number(outlet?.count_equipment || 0);
  const stands = Number(outlet?.count_stand || 0);
  return equipment > 0 || stands > 0 ? 'Есть' : 'Нет';
};
